import { useState, useEffect } from 'react';
import { doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import logger from '../lib/logger';

interface MaintenanceConfig {
  enabled: boolean;
  title?: string;
  message?: string;
  estimatedDuration?: string;
  lastUpdated?: Date | Timestamp;
}

export const useMaintenanceMode = () => {
  const [maintenanceMode, setMaintenanceMode] = useState<MaintenanceConfig>({
    enabled: false,
    title: "CLIPTHIS is under maintenance",
    message: "We're making some improvements to serve you better. We'll be back online shortly.",
    estimatedDuration: "We expect to be back online within the next few hours"
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Listen to the maintenance document in Firestore
    const maintenanceDocRef = doc(db, 'system', 'maintenance');
    
    const unsubscribe = onSnapshot(
      maintenanceDocRef,
      (doc) => {
        try {
          if (doc.exists()) {
            const data = doc.data() as MaintenanceConfig;
            setMaintenanceMode({
              enabled: data.enabled || false,
              title: data.title || "CLIPTHIS is under maintenance",
              message: data.message || "We're making some improvements to serve you better. We'll be back online shortly.",
              estimatedDuration: data.estimatedDuration || "We expect to be back online within the next few hours",
              lastUpdated: data.lastUpdated instanceof Timestamp ? data.lastUpdated.toDate() : data.lastUpdated || new Date()
            });
            logger.debug('Maintenance mode updated:', data.enabled ? 'enabled' : 'disabled');
          } else {
            // Document doesn't exist, maintenance is disabled by default
            setMaintenanceMode(prev => ({ ...prev, enabled: false }));
            logger.debug('Maintenance document does not exist, maintenance disabled');
          }
          setError(null);
        } catch (err) {
          logger.error('Error processing maintenance mode data:', err);
          setError('Failed to load maintenance status');
          // Keep maintenance disabled on error to avoid blocking users
          setMaintenanceMode(prev => ({ ...prev, enabled: false }));
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        logger.error('Error listening to maintenance mode:', err);
        setError('Failed to connect to maintenance status');
        setLoading(false);
        // Keep maintenance disabled on error to avoid blocking users
        setMaintenanceMode(prev => ({ ...prev, enabled: false }));
      }
    );

    return () => unsubscribe();
  }, []);

  return {
    maintenanceMode,
    loading,
    error
  };
};
